<script lang="ts" setup>
import { AppButton, AppLogo, AppPassword, AppTextField, AppTypography } from '@/core/components';
import { AppPasswordCriteria } from '@/core/components/fields';
import type { FormErrors } from '@/core/types/Form';
import { useVuelidate } from '@vuelidate/core';
import { ref, computed, onMounted } from 'vue';
import type { RegisterForm } from '../types/Auth';
import { useAuthStore } from '@/modules/auth/store/auth';
import { useFilterUser } from '@/store/filterUser';
import { required, email, minLength, sameAs, helpers, maxLength } from '@vuelidate/validators';
import TermsofUse from '../pages/terms-of-use.vue';
import PrivacyPolicy from '../pages/privacy-policy.vue';
import { parsedError } from '@/utils/Helpers';

const { resetFilterOptions } = useFilterUser();
const { resetOptions } = useAuthStore();
onMounted(async () => {
  await resetFilterOptions();
  await resetOptions();
});

const contactNumberOption = {
  mask: '##########',
};

const form = ref<RegisterForm>({
  first_name: '',
  last_name: '',
  mobile_number: '',
  email: '',
  password: '',
  password_confirmation: '',
});
const alpha = helpers.withMessage(
  '{label} must contain only alphabetic characters',
  helpers.regex(/^[A-Za-z]+$/)
);
const rules = () => ({
  first_name: {
    required: helpers.withMessage('First Name is required', required),
    // alpha: helpers.withMessage('First Name must contain only alphabetic characters', alpha),
  },
  last_name: {
    required: helpers.withMessage('Last Name is required', required),
    // alpha: helpers.withMessage('Last Name must contain only alphabetic characters', alpha),
  },
  mobile_number: {
    required: helpers.withMessage('Mobile Number is required', required),
    minLength: helpers.withMessage('Mobile Number must be exactly 10 digits', minLength(10)),
    maxLength: helpers.withMessage('Mobile Number must be exactly 10 digits', maxLength(10)),
    numeric: helpers.withMessage('Mobile Number must contain only digits', helpers.regex(/^\d+$/)),
  },
  email: {
    required: helpers.withMessage('Email is required', required),
    email: helpers.withMessage('Email must be valid', email),
  },
  password: {
    required: helpers.withMessage('Password is required', required),
    minLength: helpers.withMessage('Password must be at least 8 characters', minLength(8)),
    // Add your own password complexity validator if needed
  },
  password_confirmation: {
    required: helpers.withMessage('Password Confirmation is required', required),
    sameAs: helpers.withMessage('Passwords must match', sameAs(form.value.password)),
  },
});

const router = useRouter();

const v$ = useVuelidate(rules, form, { $autoDirty: true });

const { token } = await useGenerateReCaptcha('register');
const { token: loginToken } = await useGenerateReCaptcha('login');

const parsedForm = computed(() => {
  // Remove +61 prefix from mobile number if it exists
  let phoneNumber = form.value.mobile_number;
  if (phoneNumber && phoneNumber.startsWith('+61')) {
    phoneNumber = phoneNumber.substring(3); // Remove +61 prefix
  }
  
  return {
    first_name: form.value.first_name,
    last_name: form.value.last_name,
    phone_number: phoneNumber,
    email: form.value.email,
    password: form.value.password,
    password_confirmation: form.value.password_confirmation,
    recaptcha: token.value,
  };
});

// Use useSubmit for direct API control
const { data: signUpData, error: signUpError, execute: executeSignUp, pending: signUpPending } = useSubmit();
const { data, signIn } = useAuth();
const registerError = ref<{ data: FormErrors<RegisterForm> }>({ data: { errors: {}, message: '' } });
const registerLoading = ref(false);
const showTermsModal = ref(false);
const showPrivacyModal = ref(false);

const handleSignUp = async (): Promise<void> => {
  const result = await v$.value.$validate();
  if (!result) return;
  // Use the correct API endpoint and method
  const config = useRuntimeConfig();
  const apiBaseUrl = config.public.apiURL || '';
  const fullApiUrl = `${apiBaseUrl}/auth/register`;
  
  await executeSignUp(fullApiUrl, {
    method: 'POST',
    body: parsedForm.value,
  });
  
  // Check if there was an error from useSubmit
  if (signUpError.value) {  
    // Handle different error structures
    if (signUpError.value?.data?.errors) {
      // API error with field-specific errors
      registerError.value = { data: signUpError.value.data };
    } else if (signUpError.value?.errors) {
      // Direct errors object
      registerError.value = { data: { errors: signUpError.value.errors, message: signUpError.value.message || 'Registration failed' } };
    } else if (signUpError.value?.message) {
      // Simple error message
      registerError.value = { data: { errors: {}, message: signUpError.value.message } };
    } else {
      // Fallback error
      registerError.value = { data: { errors: {}, message: 'Registration failed. Please try again.' } };
    }
    registerLoading.value = false;
    return;
  }
  
  // If successful, proceed with sign in
  try {
    registerLoading.value = true;
    // Registration was already successful with useSubmit, now proceed with sign in
    await signIn(
      { username: parsedForm.value.email, password: parsedForm.value.password, token: loginToken.value },
      { redirect: false }
    );
    if (data.value) {
      router.push({ name: 'verify' });
    }
  } catch (signInError: any) {
    registerError.value = { data: { errors: {}, message: 'Registration successful but sign in failed. Please try logging in manually.' } };
  } finally {
    registerLoading.value = false;
  }
};

const errors = computed(() => {
  return parsedError<RegisterForm>(form.value, v$, registerError.value?.data?.errors);
});

// Add a computed property for general error messages
const generalError = computed(() => {
  return registerError.value?.data?.message || '';
});

const isMobile = ref(false);

onMounted(() => {
  isMobile.value = window.innerWidth <= 600;
});
</script>

<template>
  <v-card max-width="500" width="100%" variant="plain" elevation="0" class="mx-auto pa-6">
    <v-form @submit.prevent="handleSignUp">
      <AppLogo class="mx-auto my-6" height="100" width="100" />
      <AppTypography type="large" element="h1" class="text-center">SupportApp</AppTypography>
      <AppTypography type="body-2" class="text-center py-6">
        Enter your email address and password to create your account.
      </AppTypography>

      <!-- General Error Message -->
      <v-alert
        v-if="generalError"
        type="error"
        variant="tonal"
        class="mb-4"
        closable
        @click:close="() => { registerError.value = { data: { errors: {}, message: '' } } }"
      >
        {{ generalError }}
      </v-alert>

      <AppTextField
        v-model="form.first_name"
        data-cy="input-first-name"
        :error-messages="errors.first_name"
        :disabled="registerLoading"
        label="First Name"
        :required="true"
        data-testid="first-name"
        class="mb-1"
      />
      <AppTextField
        v-model="form.last_name"
        data-cy="input-last-name"
        :error-messages="errors.last_name"
        :disabled="registerLoading"
        label="Last Name"
        :required="true"
        data-testid="last-name"
        class="mb-1"
      />
      <AppTextField
        v-model="form.mobile_number"
        v-maska="contactNumberOption"
        data-cy="input-mobile-number"
        :error-messages="errors.mobile_number"
        :disabled="registerLoading"
        label="Mobile Number"
        :required="true"
        data-testid="mobile-number"
        class="mb-1"
      />
      <AppTextField
        v-model="form.email"
        data-cy="input-email"
        :error-messages="errors.email"
        :disabled="registerLoading"
        label="Email address"
        :required="true"
        data-testid="email"
        class="mb-1"
      />

      <AppPassword
        v-model="form.password"
        data-cy="input-password"
        label="Password"
        :required="true"
        :error-messages="errors.password"
        :disabled="registerLoading"
        data-testid="password"
        class="mb-1"
      />

      <AppPassword
        v-model="form.password_confirmation"
        data-cy="input-password-confirmation"
        label="Confirm password"
        :required="true"
        data-testid="password-confirmation"
        :error-messages="errors.password_confirmation"
        :disabled="registerLoading"
        class="mb-1"
      />

      <AppPasswordCriteria :password="form.password" />

      <!-- <AppTypography type="caption" class="mb-2">
        Please ensure that your first and last name match exactly as they appear on your government-issued ID.
      </AppTypography> -->

      
      <AppTypography element="span" type="subheadline-1" class="d-block text-decoration-none mb-8">
        By clicking sign up, you agree to our
        <button 
          type="button"
          @click="showTermsModal = true"
          class="text-decoration-none border-0 bg-transparent cursor-pointer"
          style="color: #6e16f4 !important;"
        >
          terms and conditions
        </button>
        and
        <button 
          type="button"
          @click="showPrivacyModal = true"
          class="text-decoration-none border-0 bg-transparent cursor-pointer"
          style="color: #6e16f4 !important;"
        >
          privacy policy
        </button>
      </AppTypography>

      <AppButton
        data-cy="button-submit"
        block
        class="mt-4 mb-8 font-weight-bold"
        type="submit"
        data-testid="submit"
        :disabled="v$.$invalid"
        :loading="registerLoading"
      >
        Sign up
      </AppButton>
      
      <AppTypography element="span" class="d-block text-center text-decoration-none">
        Already have an account?
        <nuxt-link to="/login" class="text-primary font-weight-bold text-decoration-none">Sign in</nuxt-link>
      </AppTypography>
    </v-form>
  </v-card>

  <!-- Terms and Conditions Modal -->
  <v-dialog v-model="showTermsModal" :max-width="isMobile ? '95vw' : '600px'" class="responsive-modal">
    <v-card>
      <v-card-title class="text-h5 d-flex align-center">
        <v-btn
          icon
          @click="showTermsModal = false"
          class="ml-auto"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-0">
        <TermsofUse :show-header="false" :is-onboarding="true" :showTermsModal="showTermsModal"/>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="showTermsModal = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Privacy Policy Modal -->
  <v-dialog v-model="showPrivacyModal" :max-width="isMobile ? '95vw' : '600px'" class="responsive-modal">
    <v-card>
      <v-card-title class="text-h5 d-flex align-center">
        <v-btn
          icon
          @click="showPrivacyModal = false"
          class="ml-auto"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-0">
        <PrivacyPolicy :show-header="false" :is-onboarding="true" :showPrivacyModal="showPrivacyModal"/>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="showPrivacyModal = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.v-card {
  gap: 24px;
  border-radius: 16px;
  background-color: white;

  @media (min-width: 768px) {
    padding: 32px, 16px, 32px, 16px;
  }
}

.v-card-title {
  line-height: unset !important;
  white-space: normal;
}

.v-card-subtitle {
  white-space: normal;
}

.font-footnote {
  font-weight: 500 !important;
  padding-top: 2px;
}

.responsive-modal .v-card {
  max-height: 90vh;
  overflow-y: auto;
}
@media (max-width: 600px) {
  .responsive-modal .v-card {
    max-width: 95vw !important;
    padding: 0 !important;
  }
}
</style>
